import { useRef, useEffect, useState } from "react";
import Hls from "hls.js";

// Convert RTMP/bare domain URLs to web-playable URLs
const normalizeUrl = (url) => {
  if (!url) return url;
  let u = url.trim();

  // rtmp://host/path → try Owncast HLS first
  if (u.startsWith("rtmp://") || u.startsWith("rtmps://")) {
    try {
      const stripped = u.replace(/^rtmps?:\/\//, "");
      const host = stripped.split("/")[0];
      return `https://${host}/embed/video`;
    } catch {
      return u;
    }
  }

  // Bare domain without protocol → add https
  if (!u.startsWith("http://") && !u.startsWith("https://") && !u.startsWith("rtmp")) {
    u = `https://${u}`;
  }

  return u;
};

// Detect video URL type
const detectType = (url) => {
  if (!url) return "none";
  const u = url.toLowerCase();
  if (u.includes("youtube.com/embed/") || u.includes("youtube.com/watch") || u.includes("youtu.be/") || u.includes("youtube.com/live/")) return "youtube";
  if (u.includes("twitch.tv")) return "twitch";
  if (u.includes("vimeo.com")) return "vimeo";
  if (u.includes("streamyard.com")) return "iframe";
  if (u.includes("restream.io")) return "iframe";
  if (u.endsWith(".m3u8") || u.includes(".m3u8?") || u.includes("/hls/")) return "hls";
  if (u.match(/\.(mp4|webm|ogg|mov)(\?|$)/)) return "direct";
  // Owncast or other self-hosted — /embed path
  if (u.includes("/embed")) return "iframe";
  // Default to iframe for any URL
  return "iframe";
};

// Convert YouTube/Twitch URLs to embed format
const toEmbedUrl = (url) => {
  if (!url) return url;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/live\/)([\w-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
  const ytEmbed = url.match(/youtube\.com\/embed\/([\w-]+)/);
  if (ytEmbed && !url.includes("autoplay")) return `${url}${url.includes("?") ? "&" : "?"}autoplay=1`;
  const twMatch = url.match(/twitch\.tv\/(\w+)/);
  if (twMatch && !url.includes("player.twitch.tv")) return `https://player.twitch.tv/?channel=${twMatch[1]}&parent=${window.location.hostname}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
  return url;
};

// HLS Player using hls.js
const HlsPlayer = ({ src }) => {
  const videoRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) setError("Unable to load stream. Check if the URL is active.");
      });
      return () => hls.destroy();
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS
      video.src = src;
      video.addEventListener("loadedmetadata", () => video.play().catch(() => {}));
    } else {
      setError("HLS playback not supported in this browser.");
    }
  }, [src]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-black/50 text-white/50 text-sm p-4 text-center">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <video ref={videoRef} className="w-full h-full object-contain bg-black" controls playsInline muted
      data-testid="hls-video-player"
    />
  );
};

// Universal Video Player
const SmartVideoPlayer = ({ url, pc }) => {
  const normalizedUrl = normalizeUrl(url);
  const type = detectType(normalizedUrl);

  if (type === "none") {
    return (
      <div className="flex items-center justify-center h-full bg-black/30 text-white/30 text-sm">
        No video URL configured
      </div>
    );
  }

  if (type === "hls") {
    return <HlsPlayer src={normalizedUrl} />;
  }

  if (type === "direct") {
    return (
      <video src={normalizedUrl} className="w-full h-full object-contain bg-black" controls playsInline muted
        data-testid="direct-video-player"
      />
    );
  }

  // YouTube, Twitch, Vimeo, Owncast, StreamYard — all via iframe
  const embedUrl = toEmbedUrl(normalizedUrl);
  return (
    <iframe
      src={embedUrl}
      title="Live Video"
      className="w-full h-full"
      allowFullScreen
      allow="autoplay; encrypted-media; fullscreen"
      data-testid="iframe-video-player"
    />
  );
};

export default SmartVideoPlayer;
